import React from "react";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { TrappedPanel } from "../../hooks/useDialogFocus";
import { SubTask } from "../../types";
import { FullWindowLayer } from "../ui/FullWindowLayer";

interface CompleteWithSubtasksDialogProps {
  taskTitle: string;
  incompleteSubtasks: SubTask[];
  onCancel: () => void;
  onConfirm: () => void;
  onOpenTask?: () => void;
  onOpenSubtask?: (subtaskId: string) => void;
}

const MAX_VISIBLE_SUBTASKS = 5;

const TaskTitleRef: React.FC<{ title: string; onOpenTask?: () => void }> = ({ title, onOpenTask }) => {
  if (!onOpenTask) return <>"{title}"</>;
  return (
    <button
      onClick={onOpenTask}
      className="font-medium text-warm-text-primary underline decoration-warm-text-tertiary/60 decoration-dotted underline-offset-2 hover:text-warm-blue hover:decoration-warm-blue transition-colors"
    >
      "{title}"
    </button>
  );
};

const CompleteWithSubtasksDialog: React.FC<CompleteWithSubtasksDialogProps> = ({
  taskTitle,
  incompleteSubtasks,
  onCancel,
  onConfirm,
  onOpenTask,
  onOpenSubtask,
}) => {
  useEscapeKey(onCancel);

  const count = incompleteSubtasks.length;
  const isSingle = count === 1;
  const subtaskWord = isSingle ? "subtask" : "subtasks";
  const subtaskPronoun = isSingle ? "it" : "them";
  const visible = incompleteSubtasks.slice(0, MAX_VISIBLE_SUBTASKS);
  const hiddenCount = count - visible.length;

  const title = isSingle
    ? "Complete this task and its subtask?"
    : "Complete this task and its subtasks?";

  const confirmLabel = "Complete all";

  return (
    <FullWindowLayer className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-40 p-2 sm:p-4 animate-dialog-backdrop">
      <TrappedPanel
        aria-label="Complete task with unfinished subtasks"
        className="bg-warm-bg-card rounded-xl border border-warm-border p-4 sm:p-6 w-full max-w-md max-h-[95vh] overflow-y-auto animate-dialog-panel"
      >
        <h3 className="text-lg font-semibold text-warm-text-primary mb-3">
          {title}
        </h3>
        <p className="text-warm-text-secondary text-sm mb-4">
          <TaskTitleRef title={taskTitle} onOpenTask={onOpenTask} />
          {" "}has{" "}
          <strong>
            {count} unfinished {subtaskWord}
          </strong>
          . Completing the task will complete {subtaskPronoun} too.
        </p>

        <ul className="mb-4 space-y-1.5 max-h-48 overflow-y-auto pr-1">
          {visible.map((st) => (
            <li
              key={st.id}
              className="flex items-start gap-2 text-sm text-warm-text-primary"
            >
              <span
                aria-hidden="true"
                className="mt-1.5 w-1.5 h-1.5 rounded-full bg-warm-text-tertiary flex-shrink-0"
              />
              {onOpenSubtask ? (
                <button
                  onClick={() => onOpenSubtask(st.id)}
                  className="break-words text-left underline decoration-warm-text-tertiary/60 decoration-dotted underline-offset-2 hover:text-warm-blue hover:decoration-warm-blue transition-colors"
                >
                  {st.title}
                </button>
              ) : (
                <span className="break-words">{st.title}</span>
              )}
            </li>
          ))}
          {hiddenCount > 0 && (
            <li className="text-xs text-warm-text-tertiary pl-3.5 pt-0.5">
              +{hiddenCount} more
            </li>
          )}
        </ul>

        <div className="flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-warm-bg-elevated hover:bg-warm-border-subtle text-warm-text-primary rounded-lg text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 px-4 py-2 bg-warm-blue hover:bg-warm-blue-dark text-white rounded-lg text-sm transition-colors"
          >
            {confirmLabel}
          </button>
        </div>
      </TrappedPanel>
    </FullWindowLayer>
  );
};

export default CompleteWithSubtasksDialog;
